import moment from 'moment';
import {CoursePresenceState} from '@/enums/CoursePresenceState.enum';

/**
 * Scope of a change made from the calendar panel ("Nur diese Stunde" / "Ab dieser Stunde").
 *
 * course-manager splits the course chain by week: `startingFrom` is snapped to the Monday of its week and `until`
 * is inclusive (end of the Monday-based week). Therefore:
 * - single: startingFrom = Monday 00:00 of the lesson's ISO week, until = Sunday 23:59:59.999 of the same week
 * - from:   startingFrom = Monday 00:00 of the lesson's ISO week, no until (permanent)
 * ISO weeks are used explicitly (independent of the moment locale), so Saturday and Sunday lessons stay in their week.
 */
export type ScopePreset = 'single' | 'from';

export interface ScopeDates {
    startingFrom: Date;
    /** Only for 'single' */
    until?: Date;
    /** Value for the "Vorübergehende Änderung" checkbox of the legacy edit dialogs */
    isTemporary: boolean;
}

const EVENT_FORMATS = ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD', moment.ISO_8601];

type DateInput = string | Date | moment.Moment;

function toMoment(value: DateInput): moment.Moment {
    if (typeof value === 'string') {
        const parsed = moment(value, EVENT_FORMATS, true);
        return parsed.isValid() ? parsed : moment(value);
    }
    return moment(value);
}

export function scopeDates(lessonStart: DateInput, preset: ScopePreset): ScopeDates {
    const start = toMoment(lessonStart);
    const startingFrom = start.clone().startOf('isoWeek').toDate();
    if (preset === 'single') {
        return {startingFrom, until: start.clone().endOf('isoWeek').toDate(), isTemporary: true};
    }
    return {startingFrom, isTemporary: false};
}

/** Biweekly (or rarer) courses: a single occurrence cannot be changed until Etappe 3.2 (week split shifts the rhythm) */
export function singleScopeBlockedReason(weekInterval?: number): string | undefined {
    if (weekInterval && weekInterval > 1) {
        return `Der Kurs findet nur alle ${weekInterval} Wochen statt. Einzelne Stunden können hier noch nicht geändert werden – bitte „Ab dieser Stunde“ verwenden.`;
    }
    return undefined;
}

/** Scope that is actually used: 'single' falls back to 'from' for biweekly courses */
export function effectiveScope(preset: ScopePreset, weekInterval?: number): ScopePreset {
    return preset === 'single' && singleScopeBlockedReason(weekInterval) ? 'from' : preset;
}

/** German summary shown in the dialogs instead of the date pickers */
export function scopeSummary(lessonStart: DateInput, preset: ScopePreset): string {
    const start = toMoment(lessonStart);
    const day = start.format('dd, DD.MM.YYYY');
    if (preset === 'single') {
        return `Gilt nur für die Stunde am ${day}.`;
    }
    return `Gilt ab der Stunde am ${day} für alle folgenden Stunden.`;
}

export type PastGuardKind = 'none' | 'confirm' | 'blocked';

export interface PastGuard {
    kind: PastGuardKind;
    text: string;
    /** Lesson not started yet and its only logs are absences entered in advance: changes allowed except date changes */
    absencesOnly?: boolean;
}

export const PAST_CONFIRM_TEXT = 'Diese Stunde liegt in der Vergangenheit. Die Änderung wirkt sich auf Protokolle und Abrechnung aus.';
export const RUNNING_CONFIRM_TEXT = 'Diese Stunde hat bereits begonnen. Die Änderung wirkt sich auf Protokolle und Abrechnung aus.';
export const LOGGED_BLOCK_TEXT = 'Für diese Stunde gibt es bereits Protokolle. Lehrkraft, Zeit, Raum, Ausfall und andere Änderungen am Termin sind deshalb gesperrt.';
export const ABSENCE_DATE_BLOCK_TEXT = 'Für diese Stunde ist schon eine Abwesenheit eingetragen. Zeit und Termin können deshalb nicht geändert werden, sonst passt die Abwesenheit nicht mehr zur Stunde.';

/** Course log that only records an absence („Entschuldigt“ / „Unentschuldigt“), e.g. entered in advance */
export function isAbsenceLog(log: { payload?: { state?: CoursePresenceState } } | undefined): boolean {
    const state = log?.payload?.state;
    return state === CoursePresenceState.ABSENT_WITH_PERMISSION || state === CoursePresenceState.ABSENT_WITHOUT_PERMISSION;
}

/**
 * Rule for structural changes (teacher, cancel, time, room, …) of one occurrence:
 * - occurrence not started yet and all its logs are absences entered in advance (absenceCount) → no guard, but
 *   absencesOnly: date changes stay locked (the absence is bound to the day of the lesson)
 * - any other course log exists for the occurrence → blocked
 * - occurrence has started (running or past) → confirmation
 * - otherwise no guard
 * Callers that do not pass absenceCount keep the strict rule (any log blocks).
 */
export function pastLessonGuard(lessonStart: DateInput, lessonEnd: DateInput, now: DateInput, logCount: number, absenceCount = 0): PastGuard {
    const current = toMoment(now);
    const started = !toMoment(lessonStart).isAfter(current);
    if (logCount > 0) {
        if (!started && absenceCount >= logCount) {
            return {kind: 'none', text: '', absencesOnly: true};
        }
        return {kind: 'blocked', text: LOGGED_BLOCK_TEXT};
    }
    if (started) {
        const ended = !toMoment(lessonEnd).isAfter(current);
        return {kind: 'confirm', text: ended ? PAST_CONFIRM_TEXT : RUNNING_CONFIRM_TEXT};
    }
    return {kind: 'none', text: ''};
}

/** Compact time form of the panel: weekday (0 = Monday … 6 = Sunday) and 'HH:mm' times */
export interface TimeFormValue {
    dayIndex: number;
    startTime: string;
    endTime: string;
}

const TIME_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)$/;

function minutesOf(time: string): number | undefined {
    const match = TIME_PATTERN.exec(time.trim());
    return match ? Number(match[1]) * 60 + Number(match[2]) : undefined;
}

export function timeFormOf(lessonStart: DateInput, lessonEnd: DateInput): TimeFormValue {
    const start = toMoment(lessonStart);
    return {dayIndex: start.isoWeekday() - 1, startTime: start.format('HH:mm'), endTime: toMoment(lessonEnd).format('HH:mm')};
}

/** German error text of the time form, undefined when valid */
export function timeFormError(value: TimeFormValue): string | undefined {
    const start = minutesOf(value.startTime);
    const end = minutesOf(value.endTime);
    if (start === undefined || end === undefined) {
        return 'Bitte Uhrzeiten im Format HH:MM eingeben.';
    }
    if (end <= start) {
        return 'Das Ende muss nach dem Beginn liegen.';
    }
    if (value.dayIndex < 0 || value.dayIndex > 6) {
        return 'Bitte einen Wochentag wählen.';
    }
    return undefined;
}

/**
 * New start/end ('YYYY-MM-DD HH:mm') of the lesson within its ISO week, for UpdateCourseTimeDialog
 * (which derives dayOfWeek and timeRange from them). Undefined when invalid or unchanged.
 */
export function movedLessonTimes(lessonStart: DateInput, lessonEnd: DateInput, value: TimeFormValue): { start: string; end: string } | undefined {
    if (timeFormError(value)) {
        return undefined;
    }
    const day = toMoment(lessonStart).clone().startOf('isoWeek').add(value.dayIndex, 'days').format('YYYY-MM-DD');
    const start = `${day} ${value.startTime.trim().padStart(5, '0')}`;
    const end = `${day} ${value.endTime.trim().padStart(5, '0')}`;
    const current = timeFormOf(lessonStart, lessonEnd);
    const unchanged = current.dayIndex === value.dayIndex && minutesOf(current.startTime) === minutesOf(value.startTime)
        && minutesOf(current.endTime) === minutesOf(value.endTime);
    return unchanged ? undefined : {start, end};
}

/**
 * "Kurs beenden" with a resumption date: Monday of the week with the first lesson on or after `resumeFrom`
 * (dayOfWeek: 0 = Monday … 6 = Sunday, like Weekdays).
 */
function resumeWeekStart(resumeFrom: DateInput, dayOfWeek: number): moment.Moment {
    const date = toMoment(resumeFrom).startOf('day');
    const week = date.clone().startOf('isoWeek');
    return date.isoWeekday() - 1 <= dayOfWeek ? week : week.add(1, 'week');
}

/** Date of the first lesson after the pause */
export function resumeLessonDate(resumeFrom: DateInput, dayOfWeek: number): Date {
    return resumeWeekStart(resumeFrom, dayOfWeek).add(dayOfWeek, 'days').toDate();
}

/**
 * `until` for course-manager terminate (inclusive, end of the ISO week): the week before the resumption,
 * so the course continues with the first lesson on or after `resumeFrom`.
 */
export function pauseUntilForResume(resumeFrom: DateInput, dayOfWeek: number): Date {
    return resumeWeekStart(resumeFrom, dayOfWeek).subtract(1, 'week').endOf('isoWeek').toDate();
}
